import React, { useState, useEffect } from 'react'
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { Pattern } from '../../builder/contexts/PatternContext'
import { useAlertStatus } from '../hooks/useAlertStatus'
import { getNextCandlePost } from '@greenchart/utils/dist/candle/candlePosts'

interface AlertConditionStatusProps {
  pattern: Pattern
  isActive: boolean
  inactiveMessage?: string
  inline?: boolean
  // Bar on top, counts + trigger countdown beneath, no "% complete" row
  compact?: boolean
}

const AlertConditionStatus: React.FC<AlertConditionStatusProps> = ({
  pattern,
  isActive,
  inactiveMessage = 'Activate this alert to see live status updates',
  inline = false,
  compact = false,
}) => {
  const theme = useTheme()

  const {
    buyStatusCount,
    buyTotalCount,
    sellStatusCount,
    sellTotalCount,
    isLoading,
    buyPercentage,
    sellPercentage,
  } = useAlertStatus(pattern, isActive)

  const [isTriggerActive, setIsTriggerActive] = useState(false)
  const [triggerCountdown, setTriggerCountdown] = useState<string | null>(null)

  useEffect(() => {
    if (!pattern?.trigger || !isActive) {
      setIsTriggerActive(false)
      setTriggerCountdown(null)
      return
    }

    const checkTriggerStatus = () => {
      const { trigger } = pattern
      const now = new Date().getTime()

      if (trigger.type === 'anytime') {
        setIsTriggerActive(true)
        setTriggerCountdown('Anytime')
        return
      }

      if (trigger.type === 'atPost') {
        try {
          const { chartDetails } = trigger
          let chartMs = 60000
          if (chartDetails.timeframe === 'minute') chartMs = 60000
          else if (chartDetails.timeframe === 'hour') chartMs = 3600000
          chartMs = chartMs * chartDetails.periodicity

          const nextCandleTime = getNextCandlePost(now, chartMs, 'forex')
          const timeRemaining = nextCandleTime - now

          if (timeRemaining <= 0) {
            setIsTriggerActive(true)
            setTriggerCountdown('Now')
          } else {
            const minutes = Math.floor(timeRemaining / (1000 * 60))
            const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000)
            setIsTriggerActive(minutes === 0 && seconds < 1)
            setTriggerCountdown(`${minutes}:${seconds < 10 ? '0' : ''}${seconds}`)
          }
        } catch {
          setIsTriggerActive(false)
          setTriggerCountdown('Error')
        }
      }

      if (trigger.type === 'timeBeforePost') {
        try {
          const { chartDetails } = trigger
          let chartMs = 60000
          if (chartDetails.timeframe === 'minute') chartMs = 60000
          else if (chartDetails.timeframe === 'hour') chartMs = 3600000
          chartMs = chartMs * chartDetails.periodicity

          const nextCandleTime = getNextCandlePost(now, chartMs, 'forex')

          let triggerMs = 60000
          if (trigger.timeframe === 'minute') triggerMs = 60000
          else if (trigger.timeframe === 'hour') triggerMs = 3600000

          const triggerTimeMs = nextCandleTime - trigger.value * triggerMs

          if (now >= triggerTimeMs && now <= nextCandleTime) {
            setIsTriggerActive(true)
            const timeRemaining = nextCandleTime - now
            const minutes = Math.floor(timeRemaining / (1000 * 60))
            const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000)
            setTriggerCountdown(`${minutes}:${seconds < 10 ? '0' : ''}${seconds}`)
          } else {
            setIsTriggerActive(false)
            if (now < triggerTimeMs) {
              const timeUntilTrigger = triggerTimeMs - now
              const minutes = Math.floor(timeUntilTrigger / (1000 * 60))
              const seconds = Math.floor((timeUntilTrigger % (1000 * 60)) / 1000)
              setTriggerCountdown(`${minutes}:${seconds < 10 ? '0' : ''}${seconds}`)
            } else {
              setTriggerCountdown('Wait')
            }
          }
        } catch {
          setIsTriggerActive(false)
          setTriggerCountdown('Error')
        }
      }
    }

    checkTriggerStatus()
    const intervalId = setInterval(checkTriggerStatus, 1000)
    return () => clearInterval(intervalId)
  }, [pattern, isActive])

  const hasBuyConditions = buyTotalCount > 0
  const hasSellConditions = sellTotalCount > 0
  const buyWidth = hasBuyConditions && hasSellConditions ? 50 : hasBuyConditions ? 100 : 0
  const sellWidth = hasBuyConditions && hasSellConditions ? 50 : hasSellConditions ? 100 : 0

  if (!isActive) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          my: 2,
          p: 1,
          border: '1px dashed',
          borderColor: 'text.disabled',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          {inactiveMessage}
        </Typography>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
        <CircularProgress size={24} color="primary" />
      </Box>
    )
  }

  if (buyTotalCount === 0 && sellTotalCount === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          my: 2,
          p: 1,
          border: '1px dashed',
          borderColor: 'text.disabled',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          No conditions found for this alert
        </Typography>
      </Box>
    )
  }

  if (inline) {
    return (
      <Box display="flex" alignItems="center" gap={0.75} sx={{ flexShrink: 0 }}>
        {hasBuyConditions && (
          <Typography
            variant="caption"
            sx={{ color: theme.palette.success.main, fontSize: '11px', fontWeight: 'bold' }}
          >
            {buyStatusCount}/{buyTotalCount}
          </Typography>
        )}
        <Box
          sx={{
            width: 60,
            flexShrink: 0,
            height: 5,
            borderRadius: 3,
            backgroundColor: theme.palette.divider,
            display: 'flex',
            overflow: 'hidden',
          }}
        >
          {hasBuyConditions && (
            <Box
              sx={{
                width: `${buyWidth}%`,
                height: '100%',
                position: 'relative',
                borderRight: hasSellConditions
                  ? `1px solid ${theme.palette.background.custom3 || '#121212'}`
                  : 'none',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: `${buyPercentage}%`,
                  backgroundColor: theme.palette.success.main,
                }}
              />
            </Box>
          )}
          {hasSellConditions && (
            <Box
              sx={{
                width: `${sellWidth}%`,
                height: '100%',
                position: 'relative',
                borderLeft: hasBuyConditions
                  ? `1px solid ${theme.palette.background.custom3 || '#121212'}`
                  : 'none',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  height: '100%',
                  width: `${sellPercentage}%`,
                  backgroundColor: theme.palette.error.main,
                }}
              />
            </Box>
          )}
        </Box>
        {hasSellConditions && (
          <Typography
            variant="caption"
            sx={{ color: theme.palette.error.main, fontSize: '11px', fontWeight: 'bold' }}
          >
            {sellStatusCount}/{sellTotalCount}
          </Typography>
        )}
        {triggerCountdown && (
          <Tooltip
            title={
              pattern?.trigger?.type === 'anytime'
                ? 'Can trigger anytime'
                : pattern?.trigger?.type === 'atPost'
                  ? `Triggers at the ${pattern?.trigger?.chartDetails?.periodicity} ${pattern?.trigger?.chartDetails?.timeframe} candle post`
                  : `Triggers ${pattern?.trigger?.value} ${pattern?.trigger?.timeframe}${pattern?.trigger?.value && pattern?.trigger?.value > 1 ? 's' : ''} before the ${pattern?.trigger?.chartDetails?.periodicity} ${pattern?.trigger?.chartDetails?.timeframe} candle post`
            }
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '11px',
                fontWeight: isTriggerActive ? 'bold' : 'normal',
                color: isTriggerActive ? 'success.main' : 'text.disabled',
                flexShrink: 0,
                cursor: 'default',
              }}
            >
              {triggerCountdown}
            </Typography>
          </Tooltip>
        )}
      </Box>
    )
  }

  const labelsRow = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: compact ? 0 : 0.5,
        mt: compact ? 0.75 : 0,
        position: 'relative',
      }}
    >
      {hasBuyConditions && (
        <Typography
          variant="caption"
          sx={{ color: theme.palette.success.main, fontWeight: 'bold', fontSize: '12px' }}
        >
          BUY {buyStatusCount}/{buyTotalCount}
        </Typography>
      )}

      {/* Trigger indicator */}
      <Tooltip
        title={
          pattern?.trigger?.type === 'anytime'
            ? 'This alert will trigger anytime conditions are met'
            : pattern?.trigger?.type === 'atPost'
              ? `This alert will trigger at the ${pattern?.trigger?.chartDetails?.periodicity} ${pattern?.trigger?.chartDetails?.timeframe}'s candle post`
              : `This alert will trigger ${pattern?.trigger?.value} ${pattern?.trigger?.timeframe}${pattern?.trigger?.value && pattern?.trigger?.value > 1 ? 's' : ''} before the ${pattern?.trigger?.chartDetails?.periodicity} ${pattern?.trigger?.chartDetails?.timeframe}'s candle post`
        }
      >
        <Box
          sx={{
            width: compact ? 'auto' : '20px',
            height: '100%',
            backgroundColor: theme.palette.background.custom3 || '#121212',
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          {compact ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5 }}>
              <AccessTimeIcon
                sx={{ fontSize: 16 }}
                color={isTriggerActive ? 'success' : 'disabled'}
              />
              {triggerCountdown && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '11px',
                    fontWeight: isTriggerActive ? 'bold' : 'normal',
                    color: isTriggerActive ? 'success.main' : 'text.secondary',
                  }}
                >
                  {triggerCountdown}
                </Typography>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <AccessTimeIcon fontSize="small" color={isTriggerActive ? 'success' : 'disabled'} />
            </Box>
          )}
        </Box>
      </Tooltip>

      {hasSellConditions && (
        <Typography
          variant="caption"
          sx={{ color: theme.palette.error.main, fontWeight: 'bold', fontSize: '12px' }}
        >
          SELL {sellStatusCount}/{sellTotalCount}
        </Typography>
      )}
    </Box>
  )

  const progressBar = (
    <Box
      sx={{
        height: 6,
        borderRadius: 3,
        backgroundColor: theme.palette.divider || '#333333',
        display: 'flex',
        overflow: 'hidden',
        mt: 0.5,
        mb: 0.1,
      }}
    >
      {hasBuyConditions && (
        <Box
          sx={{
            width: `${buyWidth}%`,
            height: '100%',
            position: 'relative',
            borderRight: hasSellConditions
              ? `1px solid ${theme.palette.background.custom3 || '#121212'}`
              : 'none',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${buyPercentage}%`,
              backgroundColor: theme.palette.success.main,
            }}
          />
        </Box>
      )}
      {hasSellConditions && (
        <Box
          sx={{
            width: `${sellWidth}%`,
            height: '100%',
            position: 'relative',
            borderLeft: hasBuyConditions
              ? `1px solid ${theme.palette.background.custom3 || '#121212'}`
              : 'none',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              height: '100%',
              width: `${sellPercentage}%`,
              backgroundColor: theme.palette.error.main,
            }}
          />
        </Box>
      )}
    </Box>
  )

  const statusTextRow = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, position: 'relative' }}>
      {hasBuyConditions && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
          {Math.round(buyPercentage)}% Complete
        </Typography>
      )}

      {triggerCountdown && (
        <Tooltip
          title={
            pattern?.trigger?.type === 'anytime'
              ? 'Can trigger anytime'
              : pattern?.trigger?.type === 'atPost'
                ? 'Time until next candle post'
                : 'Time until/within trigger window'
          }
        >
          <Typography
            position="absolute"
            variant="caption"
            color={isTriggerActive ? 'success' : 'text.secondary'}
            sx={{
              fontSize: '11px',
              fontWeight: isTriggerActive ? 'bold' : 'normal',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            {triggerCountdown}
          </Typography>
        </Tooltip>
      )}

      {hasSellConditions && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
          {Math.round(sellPercentage)}% Complete
        </Typography>
      )}
    </Box>
  )

  if (compact) {
    return (
      <>
        {progressBar}
        {labelsRow}
      </>
    )
  }

  return (
    <>
      {labelsRow}
      {progressBar}
      {statusTextRow}
    </>
  )
}

export default AlertConditionStatus
